import { useExport } from "@api/useExport";
import { components } from "@api/api";
import { useVisualizeMetric, VisualizeData } from "@api/useVisualizeData";
import { useMemo } from "react";
import { useVisualizeContext, VisualizeView } from "@pages/Visualize/VisualizeContext";
import { useSelectAmongContext } from "@vms/SelectAmong/SelectAmongContext";

export function useVisualizeExport(
    data: VisualizeData,
) {
    const {
        xMetricId,
        yMetricId,
        activeView,
    } = useVisualizeContext();

    const { amongCopyTableText } = useSelectAmongContext();

    const xMetric = useVisualizeMetric(xMetricId);
    const yMetric = useVisualizeMetric(yMetricId);

    const payload = useMemo(
        (): components["schemas"]["FlavorTracker.Entities.DTO.Export.VisualizePagePptExportRequest"] => {
            const columns = [
                xMetric?.name ?? '',
                yMetric?.name ?? ''
            ];

            if (activeView === VisualizeView.FamiliarFavorites) {
                columns.push('Familiar Fave');
            }

            if (activeView === VisualizeView.RisingStars) {
                columns.push('Rising Stars');
            }

            if (activeView === VisualizeView.Polarity) {
                columns.push('Polarity');
            }

            return {
                among: amongCopyTableText ?? '',
                columns,
                xAxisName: xMetric?.name ?? '',
                yAxisName: yMetric?.name ?? '',
                keywordsData: data.map(i => {
                    const xMetricData = i.metrics.find(m => m.id === xMetricId);
                    const yMetricData = i.metrics.find(m => m.id === yMetricId);
                    const columnsValues = [
                        `${xMetricData?.tableValue ?? ''}`,
                        `${yMetricData?.tableValue ?? ''}`
                    ];

                    if (activeView === VisualizeView.FamiliarFavorites) {
                        columnsValues.push(i.isFamiliarFavorites ? '*' : '');
                    }

                    if (activeView === VisualizeView.RisingStars) {
                        columnsValues.push(i.isRisingStars ? '*' : '');
                    }

                    if (activeView === VisualizeView.Polarity) {
                        columnsValues.push(`${i.polarityScore ?? ''}`);
                    }

                    return {
                        id: `${i.id}`,
                        name: i.name ?? '',
                        x: xMetricData?.sortValue ?? undefined,
                        y: yMetricData?.sortValue ?? undefined,
                        columns: columnsValues,
                    };
                }),
            };
        },
        [
            amongCopyTableText,
            data,
            xMetric,
            yMetric,
            xMetricId,
            yMetricId,
            activeView,
        ]
    );

    const { isLoading, get } = useExport(
        '/api/export/GetVisualizePagePpt',
        payload
    );

    return {
        isLoading,
        get,
    };
}